export default {
  detail: {
    all_projects_caption: "New projects appear automatically",
    title: "Personnel Planning",
    all_projects: "All projects",
    actions: "Actions",
    // `month`/`week`/`day` label the buttons on a wide toolbar; the directional wordings are the
    // tooltip and the accessible name, and are all that is left once the buttons shed their text.
    nav: {
      month: "Month",
      week: "Week",
      day: "Day",
      today: "Today",
      prev_month: "One month back",
      prev_week: "One week back",
      prev_day: "One day back",
      next_day: "One day forward",
      next_week: "One week forward",
      next_month: "One month forward",
    },
    zoom: {
      in: "Zoom in",
      out: "Zoom out",
      reset: "Reset zoom",
    },
    search: {
      projects: "Search projects",
      employees: "Search employees",
      clear: "Clear search",
      count: "{shown} of {total} shown",
    },
    sections: {
      projects: "Projects",
      employees: "Employees",
      capacity: "Capacity",
    },
    // Names the controls that hand one section the whole screen and fold the other two in and out
    // of it as thin bars.
    expand: {
      enter: "Expand section",
      exit: "Exit expanded view",
      show: "Show section",
      hide: "Hide section",
    },
    // The name of the line between two sections that is dragged up or down. For screen readers only.
    resize: {
      line: "Divider between {above} and {below}",
    },
    // The "Capacity" section: "Planned" is the sum of all phases and read-only, every phase has
    // its own line. Clicking a phase in the legend makes its line draggable.
    capacity: {
      heading: "Legend",
      planned: "Planned",
      planned_caption: "Sum of all phases",
      planned_caption_all_projects: "Sum of all projects",
      planned_row_hint: "Drag the line's points to change a day's need. The change goes to that day's phase.",
      personnel: "Staff",
      personnel_caption: "People planned in",
      edit_planned: "Set a phase's planned capacity for a period",
      edit_action: "Edit capacity",
      phase_row_hint: "Click to drag this phase's need in the chart",
      phase_row_focused: "This phase is being edited: drag the points of the coloured line. Click again to stop.",
      no_phase: "Without a phase no need can be planned",
      phases_hidden: "Phases hidden ({count})",
      phases_hidden_hint: "Search projects above to see their lines",
      add_phase: "Add phase",
      menu: {
        focus_named: "Line: {phase}",
        unfocus_named: "Stop: {phase}",
        edit_period: "Set a period …",
        edit_phase: "Edit phase …",
        preview_phase: "Phase preview",
        go_start: "Go to phase start",
        go_end: "Go to phase end",
        open_project: "Open project",
        edit_day: "Edit day …",
        edit_week: "Edit week …",
        average: "Avg. {count} people per work day",
        day_totals: "Planned {planned} · Staff {personnel} · Balance {balance}",
      },
    },
  },
  // The link to the built-in calendar of all projects in the page heading (LewAllProjectsBanner).
  overall: {
    title: "Overall planning",
    subtitle: "Every running and upcoming project in one calendar. New projects appear automatically.",
    subtitle_short: "All projects in one calendar",
    running_short: "{count} running",
    planned_short: "{planned}/{needed} today",
    planned_short_no_need: "{planned} today",
    understaffed_short: "all days staffed | {count} day understaffed | {count} days understaffed",
    running: "Running projects",
    upcoming: "no more planned | {count} more planned | {count} more planned",
    planned_today: "Planned today",
    needed_today: "of {count} people needed | of {count} person needed | of {count} people needed",
    no_need_today: "No need planned today",
    understaffed: "Understaffed days",
    understaffed_within: "in the next {days} days",
    fully_staffed: "The next {days} days are staffed",
  },
  overview: {
    title: "Personnel Planning",
    subtitle: "Plan and manage your personnel resource calendars.",
    actions: {
      create_new: "New Calendar",
    },
    multiple_projects: "Multiple projects",
    // Names the icon-only controls on each calendar card. All three repeat once per card, so the
    // calendar's name gets appended to them at the call site.
    card: {
      pin: "Pin",
      unpin: "Unpin",
      open: "Open calendar",
    },
    sections: {
      pinned: "Pinned",
      all: "All calendars",
    },
    search: {
      label: "Search calendars",
    },
    filter: {
      label: "Filter",
      heading: "Filter projects",
      show_all: "All projects",
      show_active: "Active projects",
      show_future: "Future projects",
      show_completed: "Completed projects",
    },
    sort: {
      label: "Sort",
      heading: "Sort by",
      updated_at: "Last updated",
      created_at: "Creation date",
    },
  },
  create: {
    title: "Create Personnel Planning Calendar",
    subtitle: "Create a new calendar for personnel resource planning.",
    name_label: "Calendar Name",
    projects_label: "Select Projects",
    projects_count: "{selected} of {total} projects selected",
    // Until the total is known: "2 of 2" would claim everything is selected.
    projects_count_selected: "{selected} selected",
    action: "Create",
    validation: {
      name_required: "Calendar name is required",
      projects_required: "At least one project must be selected",
    },
  },
  empty_state: {
    title: "No calendars available",
    subtitle: "Create a new calendar to get started",
  },
  pinned_empty_hint: "Drag and drop calendars here to pin them",
  unpinned_empty_hint: "Drag and drop calendars here",
  properties: {
    assignment: "Assignment",
    employee: "Employee",
    team: "Team",
    project: "Project",
    phase: "Phase",
    absence: "Absence",
    fromDate: "From-Date",
    toDate: "To-Date",
    span: "Span",
    fromTime: "From-Time",
    toTime: "To-Time",
    name: "Name",
  },
  actions_menu: {
    // Names the overflow-menu activator itself, which is an icon with no text beside it.
    label: "Calendar options",
    popout: "Calendar Popout",
    rename: "Rename",
    open_details: "Open Project Details",
    delete: "Delete",
  },
  dialog: {
    employee_add_to_calendar: {
      title: "Add Employee to Calendar",
      actions: {
        add: "Add",
        cancel: "Cancel",
      },
    },
    team_add_to_calendar: {
      title: "Add Team to Calendar",
      actions: {
        add: "Add",
        cancel: "Cancel",
      },
    },
    project_add_selector: {
      title: "Select projects to add to the calendar",
      autocomplete: {
        project: "Project",
        projects_count: "{selected} of {total} projects selected",
        // Until the server has said how many there are: a count, no claimed total.
        projects_count_selected: "{selected} selected",
        select_all: "Select all",
        clear_all: "Clear selection",
      },
      actions: {
        add: "Add",
        cancel: "Cancel",
      },
    },
    team_selector: {
      title: "Select teams to add to the calendar",
      create_title: "Create a new team",
      tab_existing: "Existing Teams",
      tab_create: "Create New Team",
      name: "Team Name",
      name_required: "Team name is required",
      color: "Team Color",
      members: "Members",
      autocomplete: {
        team: "Team",
      },
    },
    employee_add_selector: {
      title: "Select employees to add to the calendar",
      autocomplete: {
        employee: "Employee",
      },
    },
    add_team: {
      title: "Add Team",
      subtitle: "Team selection",
      autocomplete: {
        team: "Team",
        no_users: "No users in this team",
        loading: "Loading...",
        no_teams: "No teams found",
        all_teams_selected: "All teams are already selected",
      },
      actions: {
        add: "Add",
      },
    },
    remove_timeline: {
      team: {
        title: "Delete timeline?",
        text: "Do you really want to delete all team members in the timespan?",
      },
      title: "Delete timeline?",
      text: "Do you really want to delete the timespan?",
    },
    preview_project: {
      title: "Preview Project",
    },
    preview_project_phase: {
      title: "Preview Phase",
      not_found: "Phase not found",
    },
    capacity_edit: {
      title: "Edit planned capacity",
      hint: 'The "Planned" line is the sum of all phases. Here you change what one phase plans for a period.',
      phase: "Phase",
      from: "From",
      to: "To",
      mode: {
        set: "Set a fixed number",
        reset: "Reset to average",
      },
      persons: "People for this phase per day",
      average: "Phase average: {count} people per work day",
      work_days_only: "Only the project's work days",
      reset_hint:
        "The days follow the phase average again ({count} people per work day, nobody on days off). Custom values in the period are removed.",
      preview: {
        days: "{count} days in the period",
        changed: "{count} change",
        unchanged: "nothing changes in the chart",
        phase: "This phase",
        total: "Planned in total",
      },
      edit_days_in_phase: "Edit single days of the phase",
      validation: {
        required: "This field is required",
        end_after_start: "The end date must be after the start date",
        outside_phase: "The date lies outside the phase",
        not_negative: "Please enter a whole number from 0",
        no_days: "The period contains no work day of the project",
      },
      actions: {
        save: "Save",
        cancel: "Cancel",
      },
    },
    assign_project: {
      title: "Assign Project",
      subtitle: "Select a project to assign to this employee:",
      period_label: "Period",
      start_date: "Start Date",
      end_date: "End Date",
      project_period: "Project Period",
      phase_period: "Phase Period",
      autocomplete: {
        project: "Project",
        phase: "Phase",
        no_phase_found: "No phase found",
      },
      validation: {
        phase_required: "Phase is required",
        start_date_required: "Start date is required",
        end_date_required: "End date is required",
        start_after_end: "Start date must not be after end date",
        end_before_start: "End date must not be before start date",
        start_before_phase_start: "Start date must not be before phase start ({date})",
        start_before_project_start: "Start date must not be before project start ({date})",
        start_after_phase_end: "Start date must not be after phase end ({date})",
        start_after_project_end: "Start date must not be after project end ({date})",
        end_before_phase_start: "End date must not be before phase start ({date})",
        end_before_project_start: "End date must not be before project start ({date})",
        end_after_phase_end: "End date must not be after phase end ({date})",
        end_after_project_end: "End date must not be after project end ({date})",
      },
      actions: {
        assign: "Assign",
      },
    },
    multi_assign_project: {
      selected_employees: "Selected Employees",
      time_period: "Time Period",
      team_members: "Team Members",
      no_employees_selected: "No employees selected",
    },
    preview_employee: {
      title: "Preview Employee",
    },
    edit_employee: {
      title: "Edit Employee",
    },
    assign_employee: {
      title: "Assign Employee",
      subtitle: "Assign an employee to a specific Project:",
      autocomplete: {
        employee: "employee",
      },
      actions: {
        assign: "assign",
      },
    },
    assign_absence: {
      title: "Assign Absence",
      employees_label: "Employees",
      time_span_label: "Time Span",
      span_fullday: "Full Day",
      span_halfday: "Half Day",
      span_custom: "Custom",
      from_time_label: "From Time",
      to_time_label: "To Time",
      absence_type_label: "Absence Type",
      additional_options_label: "Additional Options",
      include_weekends: "Include Weekends",
      include_holidays: "Include Holidays",
      actions: {
        assign: "Assign",
      },
    },
    edit_timeline: {
      title: "Edit timeline",
      subtitle: "Edit the timeline period:",
      start_date: "Start Date",
      end_date: "End Date",
      days: "{n} day | {n} days",
      days_before: "{days} (was {before})",
      units: {
        day_1: "1 Day",
        day_5: "5 Days",
        week_1: "1 Week",
      },
      validation: {
        start_before_end: "Start date must be before end date",
        end_after_start: "End date must be after start date",
      },
    },
    calendar_rename: {
      title: "Rename Calendar",
      name_label: "Calendar Name",
      rename: "rename",
      validation: {
        required: "Calendar name is required",
      },
      actions: {
        save: "Save",
        cancel: "Cancel",
      },
    },
    team_edit: {
      title: "Edit Team",
      name_label: "Team Name",
      color_label: "Team Color",
      validation: {
        required: "Field is required",
      },
      actions: {
        save: "Save",
        cancel: "Cancel",
      },
    },
    team_members_manage: {
      title: "Manage Team Members",
      team_name: "Team Name",
      add_member: "Add Member",
      members_label: "Members",
      current_members: "Current Members",
      no_members: "No members in this team",
      actions: {
        save: "Save",
        cancel: "Cancel",
      },
    },
    employee_remove: {
      title: "Remove Employee?",
      text: "Do you really want to remove this employee including all their assignments from the calendar?",
      actions: {
        remove: "Remove",
        cancel: "Cancel",
      },
    },
    project_remove: {
      title: "Remove Project?",
      text: "Do you really want to remove this project including all its assignments from the calendar?",
      actions: {
        remove: "Remove",
        cancel: "Cancel",
      },
    },
    add_project_phase: {
      title: "Add Phase",
      project_name: "Project Name",
      phase_name: "Phase Name",
      start_date: "Start",
      end_date: "End",
      color: "Color",
      average_capacity: "Average Capacity",
      validation: {
        required: "This field is required",
        end_after_start: "End date must be after start date",
      },
      actions: {
        add: "Add",
        cancel: "Cancel",
      },
    },
    edit_project_phase: {
      title: "Edit Phase",
      project_name: "Project Name",
      phase_name: "Phase Name",
      start_date: "Start",
      end_date: "End",
      color: "Color",
      average_capacity: "Average Capacity",
      validation: {
        required: "This field is required",
        end_after_start: "End date must be after start date",
      },
      actions: {
        save: "Save",
        cancel: "Cancel",
      },
    },
    project_color_edit: {
      title: "Edit Project Color",
      color_label: "Project Color",
      actions: {
        save: "Save",
        cancel: "Cancel",
      },
    },
    calendar_delete: {
      title: "Delete Calendar?",
      text: "Do you really want to delete this calendar?",
      actions: {
        delete: "Delete",
        cancel: "Cancel",
      },
    },
  },
}
